import { Vector } from '@ikegps/core-measurement';
import type { Point } from '@ikegps/core-api';
import type { CollectionModel } from '../collection.data';
import type { CollectionModelDrawableLabel } from './label.drawable';
import type { SVGRenderOptions } from '../../../draw';
import { DEFAULT_CAPTURE_SIZE } from '../../capture';
import type { CollectionModelDrawableState } from './drawable.state';

/**
 * Insight detection classes which are rendered as marker drawables.
 */
export enum InsightMarkerDetectionClass {
	ZeroOffset = 'zero_offset',
	NineFeetMarker = 'nine_feet_marker',
	SixFeetMarker = 'six_feet_marker',
	ThreeFeetMarker = 'three_feet_marker',
	Streetlight = 'streetlight',
	Transformer = 'transformer',
	Pole_top = 'pole_top',
	Wire = 'wire',
	Guy_wire = 'guy_wire',
	Riser = 'riser',
	Cutout = 'cutout',
	Capacitor = 'capacitor',
}

const insightOffsetClassDetections: string[] = [
	InsightMarkerDetectionClass.ZeroOffset,
	InsightMarkerDetectionClass.NineFeetMarker,
	InsightMarkerDetectionClass.SixFeetMarker,
	InsightMarkerDetectionClass.ThreeFeetMarker,
];

/**
 * Check if the given insight detection class is one of the base or base offset types.
 */
export function isInsightOffsetDetectionClass(className: string): className is InsightMarkerDetectionClass {
	return insightOffsetClassDetections.includes(className);
}

/**
 * Check if the given string is one of the known insight marker detection classes.
 */
export function isInsightMarkerDetectionClass(className: string): className is InsightMarkerDetectionClass {
	return Object.values(InsightMarkerDetectionClass).includes(className as InsightMarkerDetectionClass);
}

export interface ApprovableInsightDrawable {
	approveInsightFlag(): void;

	/**
	 * Shortcut to determine if the Insight Detection is a base offset.
	 */
	isInsightBaseOffsetDetectionClass: boolean;
}

/**
 * A drawable represents one or more visualizations to display on a capture.
 * Also called an "annotation" or a "measurement" elsewhere.
 *
 * All drawables implement Point, however the (x,y) coordinate can refer to different things;
 * for example a Label Point is the top-left corner of the label,
 * and a Marker Point is the target coordinate that the marker is drawn over.
 */
export abstract class CollectionModelDrawable implements Point {
	id: string;

	protected drawableState: CollectionModelDrawableState;

	get collection(): CollectionModel {
		return this.drawableState.collection;
	}

	get captureId(): string {
		return this.drawableState.captureId;
	}

	constructor(collectionModelDrawableState: CollectionModelDrawableState, drawableId: string) {
		this.id = drawableId;
		this.drawableState = collectionModelDrawableState;

		// Add this drawable to the drawableState
		if (this.drawableState.drawables.has(this.id)) {
			throw new Error(`Tried to overwrite existing drawable with id: ${this.id}`);
		}
		this.drawableState.drawables.set(this.id, this);
	}

	abstract x: number;
	abstract y: number;

	getVector() {
		return new Vector(this.x, this.y);
	}

	protected getPointInBounds(x: number, y: number): Point {
		const capture = this.drawableState.capture;
		if (!capture) {
			return { x, y };
		}
		return {
			x: Math.min(capture.resolution.width, Math.max(0, x)),
			y: Math.min(capture.resolution.height, Math.max(0, y)),
		};
	}

	get isDeletable() {
		return true;
	}

	/** Virtual discriminator — overridden in CollectionModelDrawableMarker */
	get isMarker(): boolean {
		return false;
	}

	/** Virtual discriminator — overridden in CollectionModelDrawableMarker (base type only) */
	get isBase(): boolean {
		return false;
	}

	/** Virtual discriminator — overridden in CollectionModelDrawableMarkerOffset */
	get isBaseOffset(): boolean {
		return false;
	}

	/** Virtual discriminator — overridden in CollectionModelDrawableVerticalSeparation */
	get isVerticalSeparation(): boolean {
		return false;
	}

	/** Virtual discriminator — overridden in CollectionModelDrawableMask */
	get isMask(): boolean {
		return false;
	}

	/**
	 * The drawable was created by the user, or is an approved insight detection.
	 */
	get isUserMeasurement() {
		return !this.isInsightMeasurement;
	}

	/**
	 * The drawable is an unapproved Insight detection.
	 */
	abstract get isInsightMeasurement(): boolean;

	abstract isApprovableInsightDrawable(): this is ApprovableInsightDrawable;

	abstract get isVisible(): boolean;

	get collectionHasBase() {
		return !!this.drawableState.base;
	}

	abstract clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawable;

	/**
	 * Remove the drawable.
	 * This will also unlink any linked fields, and remove any associated drawables such as attached labels or markers.
	 */
	remove() {
		this.drawableState.drawables.delete(this.id);
	}

	protected _equals(other: CollectionModelDrawable): boolean {
		return this.id === other.id;
	}

	abstract equals(other: CollectionModelDrawable): boolean;
}

export abstract class MoveableDrawable extends CollectionModelDrawable {
	abstract move(x: number, y: number): void;

	isOnLeftSide() {
		return this.x < (this.drawableState.capture?.resolution.width ?? DEFAULT_CAPTURE_SIZE.width) / 2;
	}

	isOnRightSide() {
		return !this.isOnLeftSide();
	}
}

export interface DrawableWithLabel {
	label: CollectionModelDrawableLabel;

	/**
	 * @throws IkeFetchableRaceError due to a Capture loading race condition in geometry3D.
	 */
	getLabelConnectionPoint(options: SVGRenderOptions): Point;
}
